<script>
/* eslint-disable vue/require-v-for-key */

import { mapActions, mapState } from "pinia";
import { format } from "@/libs/date.format.js";
import upperFirst from "../../../components/methods/upperFirst.js";
import { useMainStore } from "@/stores/mainStore.js";
import datesMixins from "../../../mixins/datesMixins.js";
import Tabs from "@/components/Tabs.vue";
import ListCard from "../../../components/ListCard.vue";
import CollapsibleSection from "@/components/Collapsible/CollapsibleSection.vue";
import ButtonComponent from "@/components/Button/ButtonComponent.vue";
import EmptyMpFlow from "@/components/EmptyMpFlow/EmptyMpFlow.vue";
import ImpersonateModal from "@/components/ImpersonateModal/ImpersonateModal.vue";

export default {
    // eslint-disable-next-line vue/multi-word-component-names, vue/no-reserved-component-names
    name: "Main",
    components: {
        Tabs,
        ListCard,
        CollapsibleSection,
        ButtonComponent,
        EmptyMpFlow,
        ImpersonateModal,
    },
    mixins: [datesMixins],
    data() {
        return {
            tabs: [
                {
                    name: "advanced",
                    title: "Advanced",
                    dataTest: "sidebar-tab-advanced",
                },
                {
                    name: "simple",
                    title: "Simple",
                    dataTest: "sidebar-tab-simple",
                },
            ],
            activeTab: "advanced",

            showAddCourseModal: false,

            advanceCollapsibleList: [
                {
                    title: "Active",
                    show: false,
                },
                {
                    title: "Under review",
                    show: false,
                },
                {
                    title: "Drafts",
                    show: false,
                },
                {
                    title: "Expired",
                    show: false,
                },
            ],

            showImpersonateModal: false,
            impersonateUrl: "",

            showMobileSidePanel: false,
        };
    },
    methods: {
        ...mapActions(useMainStore, ["getData"]),

        ...mapActions(useMainStore, [
            "isEmpty",
            "capitalize",
            "returnFormattedDates",
            "returnSelectedTermFormattedDates",
            "getUniqVenues",
            "getUniqVenuesForTerm",
        ]),
        getVenueAddress(venue) {
            let string = `${venue.address?.trim()}, ${venue.city?.trim()}, ${venue.postcode?.trim()}`;
            if (string?.length > 37) {
                string = string.substring(0, 37);
                string += "...";
            }
            return string;
        },
        getTrimmedString(string, length = 37) {
            let trimmedString = "";

            if (string === null) {
                return "";
            }

            if (string?.length > length) {
                trimmedString = string.substring(0, length);
                trimmedString += "...";

                return trimmedString;
            }

            return string;
        },
        getFormattedCreateDate(created) {
            return format(created, "dS mmmm yyyy");
        },
        tabChange(tab) {
            this.$router.push({ name: tab.title });
        },
        redirectToVenue(venueId, routeName = "Simple venue") {
            this.$router.push({
                name: routeName,
                params: {
                    venueId,
                },
            });
        },

        formatVenues(venues) {
            if (venues?.length === 0) {
                return [];
            }
            return venues.map((v) => ({
                name: this.getTrimmedString(v.name),
                info: this.getVenueAddress(v),
                id: v.venue_id,
                selected: this.$route.params.venueId === v.venue_id,
            }));
        },

        formatVenuesForServices(venues) {
            if (venues?.length === 0) {
                return [];
            }
            return venues.map((v) => {
                const act = this.serviceActVenues.find((av) => av.venue_id === v.venue_id);
                const category = this.actCategories.find(
                    (ac) => ac.activity_id === act.activity_id,
                );
                const catName = this.categories.find((c) => c.category_id === category.category_id);

                const actAges = this.agesForActivities[act.activity_id];
                const ages = actAges
                    .flat()
                    .map((a) => a.name)
                    .join(", ");

                const activity = this.serviceActivities.find((a) => a.activity_id === act.activity_id);

                return {
                    name: this.getTrimmedString(activity?.title),
                    info: `${catName.name} • Ages ${ages}`,
                    id: v.venue_id,
                    selected: this.$route.params.venueId === v.venue_id,
                };
            });
        },

        redirectToRegistration() {
            if (!this.isAdmin) {
                window.onbeforeunload = null;
                window.location.href = "/register/create/advanced/add-course-details";
            } else {
                this.showImpersonateModal = true;
                this.impersonateUrl = "/register/create/advanced/add-course-details";
            }
        },

        redirectToSimpleVenueRegistration() {
            const urlGoto = "/register/create/add-venue?fromMP=yes&fromAddVenueSimple=yes";
            if (!this.isAdmin) {
                window.onbeforeunload = null;
                window.location.href = urlGoto;
            } else {
                this.showImpersonateModal = true;
                this.impersonateUrl = urlGoto;
            }
        },

        formatSingleTerm(td) {
            return {
                name: `${upperFirst(td.repetition)} • ${this.getTrimmedString(td.name, 20)}`,
                info: `${format(td.start, "dS mmmm yy")} - ${format(td.end, "dS mmmm yy")}`,
                id: td.termdate_id,
                selected: this.$route.params.termdateId === td.termdate_id,
                start: td.start,
                end: td.end,
                status: td.status,
            };
        },

        toggleSection(idx) {
            this.advanceCollapsibleList[idx].show = !this.advanceCollapsibleList[idx].show;
        },

        redirectToAdvancedTerm(termId) {
            this.$router.push({
                name: "Advanced term",
                params: { termdateId: termId },
            });
        },

        toggleMobileSidePanel() {
            this.showMobileSidePanel = !this.showMobileSidePanel;
        },
    },
    computed: {
        ...mapState(useMainStore, [
            "initialDataRetrieved",
            "businessId",
            "isAdmin",
            "allActivities",
            "serviceActivities",
            "allVenues",
            "categories",
            "allCategories",
            "actVenues",
            "simpleVenues",
            "actTermdates",
            "termDates",
            "sessions",
            "agesForActivities",
            "actCategories",
            "holidays",
            "advancedTermDates",
            "termdateVenuesRel",
            "serviceVenues",
            "serviceActVenues",
        ]),
        getVenues() {
            if (this.actVenues?.length === undefined) {
                return [];
            }

            if (this.$route.name === "Simple") {
                if (this.simpleVenues?.length > 0) {
                    if (this.$route.name === "Simple") {
                        this.redirectToVenue(this.simpleVenues[0].venue_id);
                    }

                    return this.formatVenues(this.simpleVenues);
                }
            }

            if (this.$route.name === "Simple venue" || this.$route.name === "Simple event series") {
                if (this.simpleVenues?.length > 0) {
                    return this.formatVenues(this.simpleVenues);
                }
            }

            return [];
        },

        getServiceVenues() {
            if (this.serviceVenues?.length === undefined) {
                return [];
            }

            const venues = this.serviceVenues;
            if (this.$route.name === "Services") {
                if (venues?.length > 0) {
                    if (this.$route.name === "Services") {
                        this.redirectToVenue(venues[0].venue_id, "Simple service");
                    }

                    return this.formatVenuesForServices(venues);
                }
            }

            if (
                this.$route.name === "Simple service"
                || this.$route.name === "Simple service series"
            ) {
                if (venues?.length > 0) {
                    return this.formatVenuesForServices(venues);
                }
            }

            return [];
        },

        // TODO: Think about how to rewrite this function in a less imperative and more declarative way
        formatTermdateByStatuses() {
            const filteredTerms = {
                Active: [],
                "Under review": [],
                Drafts: [],
                Expired: [],
            };

            this.advancedTermDates.forEach((term) => {
                const sessionsForTerm = this.sessions
                    .filter((s) => s.termdate_id === term.termdate_id)
                    .map((s) => s.activity_id);

                const publishedAct = this.allActivities.filter(
                    (act) => act.status === "publish" && sessionsForTerm.includes(act.activity_id),
                );

                if (new Date(term.end) < new Date() && term.status !== "draft") {
                    filteredTerms.Expired.push(this.formatSingleTerm(term));
                } else if (
                    new Date(term.end) > new Date()
                    && term.status !== "draft"
                    && publishedAct?.length > 0
                ) {
                    filteredTerms.Active.push(this.formatSingleTerm(term));
                } else if (term.status === "draft") {
                    filteredTerms.Drafts.push(this.formatSingleTerm(term));
                } else if (term.status !== "draft" && publishedAct?.length === 0) {
                    filteredTerms["Under review"].push(this.formatSingleTerm(term));
                }
            });

            // eslint-disable-next-line array-callback-return
            Object.entries(filteredTerms).map(([tabName, tabValues]) => {
                tabValues.sort((first, second) => (
                    new Date(first.start) - new Date(second.start)
                    || new Date(first.end) - new Date(second.end)
                ));
            });
            return filteredTerms;
        },

        getTermDates() {
            if (Object.values(this.advancedTermDates)?.length === 0) {
                return null;
                // return {
                //     Active: [],
                //     "Under review": [],
                //     Drafts: [],
                //     Expired: [],
                // };
            }

            if (this.$route.name === "Advanced") {
                const list = this.formatTermdateByStatuses;

                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                if (list.Active?.length > 0) {
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.advanceCollapsibleList[0].show = true;
                    return this.redirectToAdvancedTerm(list.Active[0].id);
                }

                if (list["Under review"]?.length > 0) {
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.advanceCollapsibleList[1].show = true;
                    return this.redirectToAdvancedTerm(list["Under review"][0].id);
                }

                if (list.Drafts?.length > 0) {
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.advanceCollapsibleList[2].show = true;
                    return this.redirectToAdvancedTerm(list.Drafts[0].id);
                }

                if (list.Expired?.length > 0) {
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.advanceCollapsibleList[3].show = true;
                    return this.redirectToAdvancedTerm(list.Expired[0].id);
                }
            }

            if (this.$route.name === "Advanced term") {
                const list = this.formatTermdateByStatuses;
                if (
                    list.Active?.length > 0
                    && list.Active.filter((el) => el.id === this.$route.params.termdateId)?.length > 0
                ) {
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.advanceCollapsibleList[0].show = true;
                } else if (
                    list["Under review"]?.length > 0
                    && list["Under review"].filter((el) => el.id === this.$route.params.termdateId)
                        ?.length > 0
                ) {
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.advanceCollapsibleList[1].show = true;
                } else if (
                    list.Drafts?.length > 0
                    && list.Drafts.filter((el) => el.id === this.$route.params.termdateId)?.length > 0
                ) {
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.advanceCollapsibleList[2].show = true;
                } else if (
                    list.Expired?.length > 0
                    && list.Expired.filter((el) => el.id === this.$route.params.termdateId)?.length > 0
                ) {
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.advanceCollapsibleList[3].show = true;
                }

                return list;
            }

            if (this.$route.name === "Advanced event series") {
                const obj = Object.values(this.formatTermdateByStatuses).map((items, idx) => items.map((el) => {
                    if (
                        this.$route.params.termId !== undefined
                        && el.id === this.$route.params.termId
                    ) {
                        // eslint-disable-next-line no-param-reassign
                        el.selected = true;
                        this.advanceCollapsibleList[idx].show = true;
                    }
                    return el;
                }));

                return {
                    Active: obj[0],
                    "Under review": obj[1],
                    Drafts: obj[2],
                    Expired: obj[3],
                };
            }

            return [];
        },

        getCurrentActiveTab() {
            return this.$route?.matched[0].name?.toLowerCase();
        },

        showEmptyFlowCard() {
            if (this.$route?.matched[0].name === "Advanced" && this.getTermDates === null) {
                return true;
            }

            if (this.$route?.matched[0].name === "Simple" && this.getVenues?.length === 0) {
                return true;
            }

            if (this.$route?.matched[0].name === "Services" && this.getServiceVenues?.length === 0) {
                return true;
            }

            return false;
        },

        isSimpleRoute() {
            return this.$route?.matched[0].name === "Simple";
        },

        isServiceRoute() {
            return this.$route?.matched[0].name === "Services";
        },

        isAdvancedRoute() {
            return this.$route?.matched[0].name === "Advanced";
        },
    },
    async created() {
        if (this.$route.name === "Main") {
            this.$router.push({ name: "Advanced" });
        }

        await this.getData();
    },
    mounted() {
        setTimeout(() => {
            if (this.venues !== null) {
                // this.allCategories = this.categories;
            }

            const tIdx = this.tabs.findIndex((t) => t.title === this.$route?.matched[0].name);
            this.$refs.tabs.setActiveTab(tIdx);
        }, 500);
    },
};
</script>

<template>
    <button
        v-bind:class="[showMobileSidePanel? 'side-bar-mobile-button-active' : '' , 'btn btn-new side-bar-mobile-button']"
        @click="toggleMobileSidePanel"
    >
        {{ showMobileSidePanel ? "> Hide" : " < Show" }} side menu {{ showMobileSidePanel ? "<" : "" }}
    </button>
    <div v-bind:class="[showMobileSidePanel? 'side-bar-active' : '' , 'cards-list position-relative side-bar']">
        <div class="d-flex flex-row tab-block">
            <Tabs :tabs="tabs" @change="tabChange" :full-width="true" ref="tabs"/>
        </div>
        <ListCard
            v-if="getVenues?.length > 0 && isSimpleRoute"
            :items="getVenues"
            class="simple-venues"
            @card-click="$router.push({ name: 'Simple venue', params: { venueId: $event } })"
        />

        <ListCard
            v-if="isServiceRoute && getServiceVenues?.length > 0"
            :items="getServiceVenues"
            class="services"
            @card-click="$router.push({ name: 'Simple service', params: { venueId: $event } })"
        />

        <div v-if="isAdvancedRoute" class="advanced-list">
            <div v-for="(collapsibleItem, idx) in advanceCollapsibleList">
                <CollapsibleSection
                    v-if="getTermDates"
                    :title="collapsibleItem.title"
                    :show="collapsibleItem.show"
                    :count="getTermDates[collapsibleItem.title]?.length"
                    @section-click="toggleSection(idx)"
                >
                    <ListCard
                        v-if="getTermDates[collapsibleItem.title]?.length > 0"
                        :items="getTermDates[collapsibleItem.title] || []"
                        :is-scroll-enabled="false"
                        @card-click="
                            $router.push({ name: 'Advanced term', params: { termdateId: $event } })
                        "
                    />
                </CollapsibleSection>
            </div>
        </div>

        <div
            v-if="isAdvancedRoute"
            class="align-items-center position-absolute add-button-container"
        >
            <div class="">
                <ButtonComponent
                    @click="redirectToRegistration"
                    class="add-new-date-range"
                    dataTest="btn-add-datarange">
                    + Add a date range
                </ButtonComponent>
            </div>
        </div>
        <div
            v-if="isSimpleRoute"
            class="align-items-center position-absolute add-button-container"
        >
            <div class="">
                <ButtonComponent
                    @click="redirectToSimpleVenueRegistration"
                    class="add-new-date-range"
                    dataTest="btn-add-venue">
                    + Add a venue
                </ButtonComponent>
            </div>
        </div>
    </div>

    <div v-bind:class="[showMobileSidePanel? 'right-pane-inactive' : '' , 'right-pane']">
        <div class="right-pane-content">
            <router-view :key="$route?.fullPath"/>
            <div v-if="showEmptyFlowCard && initialDataRetrieved">
                <EmptyMpFlow :type="getCurrentActiveTab" :is-admin="isAdmin"/>
            </div>
        </div>
    </div>

    <ImpersonateModal
        :show-modal="showImpersonateModal"
        :url="impersonateUrl"
        @close="showImpersonateModal = false"
    />
</template>
<style scoped>
.advanced-list {
    max-height: calc(100vh - 84px - 90px - 53px);
    margin-top: 10px;
    /*overflow-x: hidden;*/
    padding-right: 10px;
    overflow-y: scroll;
    overflow-x: hidden;
    scrollbar-width: none;
}

.card-list {
    background-color: rgba(221, 221, 221, 0.25);
}

.tab-block {
    height: 77px;
    background-color: rgba(221, 221, 221, 0.5);
    margin-left: -30px;
    margin-right: -10px;
    display: flex;
    align-items: flex-end;
}

.side-bar-mobile-button {
    display: none;
    position: fixed;
    top: 55px;
    left: 0;
    z-index: 1;
    padding: 10px;
    border: 1px solid #000;
    border-radius: 5px;
    cursor: pointer;
    transition: all ease-in-out 0.3s;
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
    margin: 0;
}

@media only screen and (max-width: 768px) {
    .management-panel {
        min-height: 20vh;
    }

    .management-panel .side-bar:not(.side-bar-active) {
        display: none;
    }

    .side-bar-mobile-button {
        display: block;
    }

    .side-bar-mobile-button-active {
        background: white;
        color: black !important;
        left: 3px;
        right: 23px;
        top: 45px;
        border-bottom-left-radius: 5px;
        border-top-left-radius: 5px;
    }

    .align-items-center.position-absolute.add-button-container {
        position: fixed !important;
    }

    .right-pane {
        max-width: 100%;
    }

    .right-pane-inactive {
        display: none;
    }

    .right-pane-content {
        min-width: 100%;
        max-width: 100%;
    }
}
</style>
